import { useEffect, useMemo, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { last } from 'lodash-es'
import { FormControlLabel, Radio, RadioGroup, Stack } from '@mui/material'
import { DataGridPremium, GridRenderCellParams } from '@mui/x-data-grid-premium'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import RipLineSceneMarker from '@sceneMarkers/RipLineSceneMarker/marker'
import {
  parseRelativePosition,
  ripPreviewLinesFromWallsAndPoint,
  ripPreviewLinesFromWallsAndRelativePosition,
} from '@sceneMarkers/RipLineSceneMarker/utils'
import { CoordinatePointInput, RelativePositionField } from '@ui/forms'
import { useControlStore } from '@editorStores'
import { useElementLabel, useGuidToElement, useSceneMarker } from '@editorHooks'
import { DefaultValues, RipCreationMode, WallRipConfig } from '../../types'
import AddRipCheckbox from '../AddRipCheckbox'

interface Props {
  hideElementColumn: boolean
  length?: number
}

const FormFields = ({ hideElementColumn = false, length }: Props) => {
  const [sceneMarker, setSceneMarker] = useState<ImmutableVector3 | null>(null)

  useSceneMarker({
    marker: sceneMarker as unknown as ImmutableVector3,
    enabled: !!sceneMarker,
  })

  const { setValue } = useFormContext()
  const config = useWatch<DefaultValues>({ name: 'config' }) as WallRipConfig
  const ripCreationMode = useWatch<DefaultValues>({ name: 'ripCreationMode' }) as RipCreationMode
  const copyRipsOriginWall = useWatch<DefaultValues>({ name: 'copyRipsOriginWall' }) as
    | string
    | null
  const ripPoint = useWatch<DefaultValues>({ name: 'ripPoint' }) as Point | undefined
  const relPosition = useWatch<DefaultValues>({ name: 'relPosition' })

  const setCopyRipsOriginWallGuid = useControlStore(state => state.setCopyRipsOriginWallGuid)
  const getLabel = useElementLabel()

  useEffect(() => {
    if (ripCreationMode === 'copyRips' && copyRipsOriginWall) {
      setCopyRipsOriginWallGuid(copyRipsOriginWall)
    } else {
      setCopyRipsOriginWallGuid(null)
    }
    return () => setCopyRipsOriginWallGuid(null)
  }, [ripCreationMode, copyRipsOriginWall, setCopyRipsOriginWallGuid])

  const elementGuids = Object.keys(config)

  const guidToElement = useGuidToElement()

  const columns = useMemo(() => {
    const elementColumn = {
      field: 'id',
      headerName: 'Element',
      flex: 0,
      renderCell: (params: GridRenderCellParams) => getLabel(params.id as string),
    }

    const ripColumns = [
      {
        field: 'start',
        headerName: 'Rippe Start',
        flex: 1,
        renderCell: (params: GridRenderCellParams) => (
          <AddRipCheckbox
            guid={params.id as string}
            position="start"
            onMouseEnter={() => {
              const wall = guidToElement[params.id as string] as ShapeObject
              const basePoint = wall.shape.points[0]
              const lastPoint = last(wall.shape.points) as ImmutableVector3

              const marker = new ImmutableVector3(
                basePoint.x,
                basePoint.y,
                basePoint.z + (lastPoint.distanceTo(basePoint) || 0) / 2,
              )

              setSceneMarker(marker)
            }}
            onMouseLeave={() => {
              setSceneMarker(null)
            }}
          />
        ),
        nowrap: true,
      },
      {
        field: 'end',
        headerName: 'Rippe Ende',
        flex: 1,
        renderCell: (params: GridRenderCellParams) => (
          <AddRipCheckbox
            guid={params.id as string}
            position="end"
            onMouseEnter={() => {
              const wall = guidToElement[params.id as string] as ShapeObject

              const basePoint = wall.shape.points[1]
              const nextPoint = wall.shape.points[2]

              const marker = new ImmutableVector3(
                basePoint.x,
                basePoint.y,
                basePoint.z + (nextPoint.distanceTo(basePoint) || 0) / 2,
              )

              setSceneMarker(marker)
            }}
            onMouseLeave={() => {
              setSceneMarker(null)
            }}
          />
        ),
        nowrap: true,
      },
    ]

    if (hideElementColumn) return ripColumns

    return [elementColumn, ...ripColumns]
  }, [getLabel, guidToElement, hideElementColumn])

  const rows: {
    id: string
    start: boolean | undefined
    end: boolean | undefined
  }[] = useMemo(() => {
    return elementGuids.map(elementGuid => {
      return {
        id: elementGuid,
        start: undefined,
        end: undefined,
      }
    })
  }, [elementGuids])

  const ripPreviewLines = useMemo(() => {
    const walls = elementGuids
      .map(guid => guidToElement[guid] as ShapeObject | undefined)
      .filter((wall): wall is ShapeObject => wall !== undefined)

    if (ripCreationMode === 'point' && ripPoint) {
      return ripPreviewLinesFromWallsAndPoint(walls, ripPoint)
    }

    const parsedRelPosition = parseRelativePosition(relPosition)
    if (ripCreationMode === 'relative' && parsedRelPosition !== undefined) {
      return ripPreviewLinesFromWallsAndRelativePosition(walls, parsedRelPosition)
    }

    return undefined
  }, [elementGuids, guidToElement, ripCreationMode, ripPoint, relPosition])

  return (
    <Stack direction="column" spacing={1}>
      {(ripCreationMode === 'point' || ripCreationMode === 'relative') && (
        <RipLineSceneMarker lines={ripPreviewLines} />
      )}
      <DataGridPremium
        rows={rows}
        columns={columns}
        hideFooterPagination
        hideFooterSelectedRowCount
        hideFooter
        autosizeOnMount
        autosizeOptions={{ includeHeaders: false, expand: true }}
        disableColumnSorting
        disableColumnFilter
        disableColumnMenu
        disableRowSelectionOnClick
        density="compact"
        columnHeaderHeight={35}
        rowHeight={35}
      />

      <RadioGroup
        row
        value={ripCreationMode}
        onChange={e => {
          const mode = e.target.value as RipCreationMode
          setValue('ripCreationMode', mode)
          setValue('useRelativePosition', mode === 'relative')
          setValue('usePointPosition', mode === 'point')
          if (mode !== 'copyRips') setValue('copyRipsOriginWall', null)
        }}
      >
        <FormControlLabel value="startAndEnd" control={<Radio size="small" />} label="Start/Ende" />
        {length && (
          <FormControlLabel value="relative" control={<Radio size="small" />} label="Rel. Pos." />
        )}
        <FormControlLabel value="point" control={<Radio size="small" />} label="3D Punkt" />
      </RadioGroup>

      {ripCreationMode === 'relative' && length && (
        <Stack direction="row" spacing={1} alignItems="center">
          <RelativePositionField name="relPosition" length={length} sx={{ width: '100px' }} />
        </Stack>
      )}

      {ripCreationMode === 'point' && (
        <Stack direction="row" spacing={1} alignItems="center">
          <CoordinatePointInput label="Punkt (3D)" name="ripPoint" />
        </Stack>
      )}
    </Stack>
  )
}

export default FormFields
